import React, { useState, useEffect } from 'react';
import {
    Modal,
    Table,
    Button,
    Form,
    InputNumber,
    Switch,
    Space,
    message,
    Popconfirm,
    Tag,
    Typography,
    Divider,
    Collapse,
    Select
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SettingOutlined
} from '@ant-design/icons';
import { salesApi } from '../../../services/salesApi';
import { useSales } from '../SalesContext';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface ProductAdjustment {
    product_id: number;
    product_name: string;
    base_commission: number;
    adjusted_commission: number | null;
    has_adjustment: boolean;
}

interface Breakpoint {
    id?: number;
    threshold: number;
    order: number;
    is_active: boolean;
    business_area?: string;
    products?: ProductAdjustment[];
}

interface BreakpointConfigModalProps {
    visible: boolean;
    onClose: () => void;
}

const BreakpointConfigModal: React.FC<BreakpointConfigModalProps> = ({ visible, onClose }) => {
    const [breakpoints, setBreakpoints] = useState<Breakpoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [productForm] = Form.useForm();
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formVisible, setFormVisible] = useState(false);
    const [productAdjustments, setProductAdjustments] = useState<{ [key: number]: number | null }>({});
    const [selectedBusinessArea, setSelectedBusinessArea] = useState<string>('telecomunicacao');
    const { products, sections } = useSales();

    useEffect(() => {
        if (visible) {
            fetchBreakpoints();
            setSelectedBusinessArea('telecomunicacao');
            setProductAdjustments({});
        }
    }, [visible]);

    // Get products based on selected business area
    const areaProducts = React.useMemo(() => {
        const areaSections = sections.filter(s => s.business_area === selectedBusinessArea && s.is_active);
        const sectionIds = areaSections.map(s => s.id);
        return products.filter(p => sectionIds.includes(p.section_id) && p.is_active);
    }, [products, sections, selectedBusinessArea]);

    const fetchBreakpoints = async () => {
        setLoading(true);
        try {
            const data = await salesApi.getCommissionBreakpointsAdmin();
            setBreakpoints(data);
        } catch (error) {
            message.error('Erro ao carregar breakpoints');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values: any) => {
        try {
            // Use the business_area from the form values (which should match selectedBusinessArea)
            const businessArea = values.business_area || selectedBusinessArea;
            
            // Get products for the selected business area
            const areaSections = sections.filter(s => s.business_area === businessArea && s.is_active);
            const sectionIds = areaSections.map(s => s.id);
            const productsForArea = products.filter(p => sectionIds.includes(p.section_id) && p.is_active);
            
            // Prepare products array with adjustments
            const productsData = productsForArea.map(product => ({
                product_id: product.id,
                adjusted_commission: productAdjustments[product.id] !== null && productAdjustments[product.id] !== undefined
                    ? productAdjustments[product.id]
                    : null
            })).filter(p => p.adjusted_commission !== null); // Only include products with adjustments

            const payload = {
                ...values,
                business_area: businessArea,
                products: productsData
            };

            if (editingId) {
                await salesApi.updateCommissionBreakpoint(editingId, payload);
                message.success('Breakpoint atualizado com sucesso');
            } else {
                await salesApi.createCommissionBreakpoint(payload);
                message.success('Breakpoint criado com sucesso');
            }
            
            form.resetFields();
            productForm.resetFields();
            setProductAdjustments({});
            setSelectedBusinessArea('telecomunicacao'); // Reset to default
            setFormVisible(false);
            setEditingId(null);
            fetchBreakpoints();
        } catch (error: any) {
            message.error(error.response?.data?.error || 'Erro ao salvar breakpoint');
        }
    };

    const handleEdit = (record: Breakpoint) => {
        setEditingId(record.id!);
        const businessArea = record.business_area || 'telecomunicacao';
        setSelectedBusinessArea(businessArea);
        form.setFieldsValue({
            threshold: record.threshold,
            order: record.order,
            is_active: record.is_active,
            business_area: businessArea
        });
        
        // Set product adjustments
        const adjustments: { [key: number]: number | null } = {};
        if (record.products) {
            record.products.forEach(p => {
                if (p.has_adjustment && p.adjusted_commission !== null) {
                    adjustments[p.product_id] = p.adjusted_commission;
                }
            });
        }
        setProductAdjustments(adjustments);
        
        setFormVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await salesApi.deleteCommissionBreakpoint(id);
            message.success('Breakpoint eliminado com sucesso');
            fetchBreakpoints();
        } catch (error) {
            message.error('Erro ao eliminar breakpoint');
        }
    };

    const handleAddNew = () => {
        setEditingId(null);
        setSelectedBusinessArea('telecomunicacao'); // Reset to default
        form.resetFields();
        productForm.resetFields();
        setProductAdjustments({});
        // Set initial values after reset
        form.setFieldsValue({
            is_active: true,
            order: breakpoints.length + 1,
            business_area: 'telecomunicacao'
        });
        setFormVisible(true);
    };

    const handleCancel = () => {
        form.resetFields();
        productForm.resetFields();
        setProductAdjustments({});
        setSelectedBusinessArea('telecomunicacao'); // Reset to default
        setFormVisible(false);
        setEditingId(null);
    };

    const handleProductAdjustmentChange = (productId: number, value: number | null) => {
        setProductAdjustments(prev => ({
            ...prev,
            [productId]: value
        }));
    };

    const columns = [
        {
            title: 'Ordem',
            dataIndex: 'order',
            key: 'order',
            width: 80,
            sorter: (a: Breakpoint, b: Breakpoint) => a.order - b.order,
        },
        {
            title: 'Área de Negócio',
            dataIndex: 'business_area',
            key: 'business_area',
            width: 150,
            render: (area: string) => {
                const areaMap: { [key: string]: { label: string; color: string } } = {
                    'telecomunicacao': { label: 'Telecomunicação', color: 'blue' },
                    'seguranca': { label: 'Segurança', color: 'orange' }
                };
                const areaInfo = areaMap[area || 'telecomunicacao'] || { label: area || 'Telecomunicação', color: 'default' };
                return <Tag color={areaInfo.color}>{areaInfo.label}</Tag>;
            },
        },
        {
            title: 'Threshold (Instalados)',
            dataIndex: 'threshold',
            key: 'threshold',
            width: 150,
            render: (value: number) => <Text strong>{value}</Text>,
        },
        {
            title: 'Produtos Ajustados',
            key: 'products_count',
            width: 150,
            render: (_: any, record: Breakpoint) => {
                const adjustedCount = record.products?.filter(p => p.has_adjustment).length || 0;
                const totalCount = record.products?.length || 0;
                return (
                    <Text>
                        {adjustedCount} / {totalCount} produtos
                    </Text>
                );
            },
        },
        {
            title: 'Estado',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 100,
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'Ativo' : 'Inativo'}
                </Tag>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            width: 150,
            render: (_: any, record: Breakpoint) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Editar
                    </Button>
                    <Popconfirm
                        title="Eliminar breakpoint?"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="Sim"
                        cancelText="Não"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Eliminar
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <SettingOutlined />
                    <span>Configuração de Breakpoints de Comissão</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1200}
            destroyOnClose
        >
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                    Configure os breakpoints que alteram os valores base de comissão dos produtos. Para Telecomunicação,
                    os breakpoints são baseados no número total de Instalados do mês atual (empresa). Para Segurança,
                    os breakpoints são baseados no número de Instalados do mês atual do utilizador. Para cada breakpoint,
                    defina manualmente o valor ajustado para cada produto (apenas os produtos que deseja alterar).
                </Text>
            </div>

            <Divider />

            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5} style={{ margin: 0 }}>Breakpoints Configurados</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddNew}
                >
                    Adicionar Breakpoint
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={breakpoints}
                loading={loading}
                rowKey="id"
                pagination={false}
                expandable={{
                    expandedRowRender: (record: Breakpoint) => {
                        if (!record.products || record.products.length === 0) {
                            return <Text type="secondary">Nenhum produto configurado</Text>;
                        }
                        return (
                            <Table
                                columns={[
                                    { 
                                        title: 'Produto', 
                                        dataIndex: 'product_name', 
                                        key: 'product_name',
                                        render: (name: string, record: any) => {
                                            // Find the product to get its section_id
                                            const product = products.find(p => p.id === record.product_id);
                                            if (product) {
                                                const section = sections.find(s => s.id === product.section_id);
                                                return (
                                                    <span>
                                                        {name}
                                                        {section && (
                                                            <span style={{ color: '#999', marginLeft: 4 }}>
                                                                ({section.name})
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            }
                                            return name;
                                        }
                                    },
                                    {
                                        title: 'Comissão Base',
                                        dataIndex: 'base_commission',
                                        key: 'base_commission',
                                        render: (value: number) => `€${value.toFixed(2)}`
                                    },
                                    {
                                        title: 'Comissão Ajustada',
                                        dataIndex: 'adjusted_commission',
                                        key: 'adjusted_commission',
                                        render: (value: number | null) => value !== null ? `€${value.toFixed(2)}` : <Text type="secondary">Sem ajuste</Text>
                                    },
                                ]}
                                dataSource={record.products}
                                rowKey="product_id"
                                pagination={false}
                                size="small"
                            />
                        );
                    },
                    rowExpandable: () => true,
                }}
            />

            {formVisible && (
                <Modal
                    title={editingId ? 'Editar Breakpoint' : 'Novo Breakpoint'}
                    open={formVisible}
                    onCancel={handleCancel}
                    footer={null}
                    width={900}
                    destroyOnClose
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            is_active: true,
                            order: breakpoints.length + 1,
                            business_area: 'telecomunicacao'
                        }}
                    >
                        <Form.Item
                            name="business_area"
                            label="Área de Negócio"
                            rules={[{ required: true, message: 'Por favor, selecione a área de negócio' }]}
                        >
                            <Select
                                style={{ width: '100%' }}
                                value={selectedBusinessArea}
                                onChange={(value) => {
                                    setSelectedBusinessArea(value);
                                    form.setFieldsValue({ business_area: value }); // Sync with form
                                    setProductAdjustments({}); // Reset adjustments when changing area
                                }}
                            >
                                <Select.Option value="telecomunicacao">Telecomunicação (Empresa)</Select.Option>
                                <Select.Option value="seguranca">Segurança (Utilizador)</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="threshold"
                            label="Threshold (Número de Instalados)"
                            rules={[{ required: true, message: 'Por favor, insira o threshold' }]}
                        >
                            <InputNumber
                                min={1}
                                style={{ width: '100%' }}
                                placeholder="Ex: 50"
                            />
                        </Form.Item>

                        <Form.Item
                            name="order"
                            label="Ordem"
                            rules={[{ required: true, message: 'Por favor, insira a ordem' }]}
                        >
                            <InputNumber
                                min={1}
                                style={{ width: '100%' }}
                                placeholder="Ordem do breakpoint"
                            />
                        </Form.Item>

                        <Form.Item
                            name="is_active"
                            label="Estado"
                            valuePropName="checked"
                        >
                            <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
                        </Form.Item>

                        <Divider>Configuração de Produtos</Divider>

                        <div style={{ marginBottom: 16 }}>
                            <Text type="secondary">
                                Defina o valor ajustado de comissão para cada produto. Deixe em branco para manter o valor base.
                            </Text>
                        </div>

                        <Table
                            dataSource={areaProducts}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Produto',
                                    dataIndex: 'name',
                                    key: 'name',
                                    width: 200,
                                    render: (name: string, record: any) => {
                                        const section = sections.find(s => s.id === record.section_id);
                                        return (
                                            <span>
                                                {name}
                                                {section && (
                                                    <span style={{ color: '#999', marginLeft: 4 }}>
                                                        ({section.name})
                                                    </span>
                                                )}
                                            </span>
                                        );
                                    },
                                },
                                {
                                    title: 'Comissão Base',
                                    dataIndex: 'commission',
                                    key: 'commission',
                                    width: 120,
                                    render: (value: number) => (
                                        <Text strong>€{value.toFixed(2)}</Text>
                                    ),
                                },
                                {
                                    title: 'Comissão Ajustada',
                                    key: 'adjusted_commission',
                                    width: 200,
                                    render: (_: any, record: any) => (
                                        <InputNumber
                                            min={0}
                                            step={0.01}
                                            style={{ width: '100%' }}
                                            placeholder="Deixe vazio para manter base"
                                            value={productAdjustments[record.id] !== null && productAdjustments[record.id] !== undefined
                                                ? productAdjustments[record.id]
                                                : undefined}
                                            onChange={(value) => handleProductAdjustmentChange(record.id, value || null)}
                                            formatter={(value) => value ? `€${value}` : ''}
                                            parser={(value) => {
                                                const cleaned = value?.replace('€', '').replace(/\s/g, '') || '';
                                                const parsed = parseFloat(cleaned);
                                                return isNaN(parsed) ? 0 : parsed;
                                            }}
                                        />
                                    ),
                                },
                                {
                                    title: 'Diferença',
                                    key: 'difference',
                                    width: 120,
                                    render: (_: any, record: any) => {
                                        const adjusted = productAdjustments[record.id];
                                        if (adjusted === null || adjusted === undefined) {
                                            return <Text type="secondary">-</Text>;
                                        }
                                        const diff = adjusted - record.commission;
                                        const color = diff > 0 ? '#52c41a' : diff < 0 ? '#ff4d4f' : '#595959';
                                        return (
                                            <Text style={{ color }}>
                                                {diff > 0 ? '+' : ''}€{diff.toFixed(2)}
                                            </Text>
                                        );
                                    },
                                },
                            ]}
                        />

                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={handleCancel}>
                                Cancelar
                            </Button>
                            <Button type="primary" onClick={() => form.submit()}>
                                {editingId ? 'Atualizar' : 'Criar'} Breakpoint
                            </Button>
                        </div>
                    </Form>
                </Modal>
            )}
        </Modal>
    );
};

export default BreakpointConfigModal;
